import { format } from "date-fns";

import type { FetalHeartRateFlowsheetFormValues } from "@/encounters/components/EncounterLabor/flowsheet-forms/fetal-heart-rate/types/fetal-heart-rate-form";
import type { FetalHeartRateFlowsheetFormSchemaType } from "@/encounters/components/EncounterLabor/flowsheet-forms/fetal-heart-rate/zod-schemas/fetal-heart-rate-flowsheet-form";
import { localToUtcTimeString } from "@/utils/timeUtils";

/** Empty hydrate shape (null datetime). Used when editing/mapping a QR — not for a new quick-add form. */
export function getEmptyFetalHeartRateFlowsheetFormValues(): FetalHeartRateFlowsheetFormValues {
  return {
    fhrDate: null,
    fhrTime: null,
    fhrBPMLow: "",
    fhrBPMHigh: "",
    fhrDurationOfListening: "",
    fhrListeningDevice: null,
    fhrRelationToContractions: [],
    accelerationsDetected: null,
    decelerationsDetected: null,
    assessBaseline: null,
    fhrBaseline: "",
    fhrDurationOfListeningBaseline: "",
    fhrNotes: "",
    fhrSignatureInitials: [],
  };
}

/** Defaults for a new quick-add form (current local date/time prefilled). */
export function getDefaultFetalHeartRateFlowsheetFormValues(): FetalHeartRateFlowsheetFormValues {
  const now = new Date();
  const localTime = format(now, "HH:mm:ss");
  return {
    ...getEmptyFetalHeartRateFlowsheetFormValues(),
    fhrDate: now,
    fhrTime: localToUtcTimeString(localTime),
  };
}

export function normalizeFetalHeartRateFlowsheetFormValues(
  parsed: FetalHeartRateFlowsheetFormSchemaType,
): FetalHeartRateFlowsheetFormValues {
  // Preserve explicit null date/time (cleared or omitted clinical datetime). Do not fall back to
  // "now" via ?? — that belongs only on new-form defaults.
  const empty = getEmptyFetalHeartRateFlowsheetFormValues();
  return {
    fhrDate: parsed.fhrDate !== undefined ? parsed.fhrDate : empty.fhrDate,
    fhrTime: parsed.fhrTime !== undefined ? parsed.fhrTime : empty.fhrTime,
    fhrBPMLow: parsed.fhrBPMLow ?? empty.fhrBPMLow,
    fhrBPMHigh: parsed.fhrBPMHigh ?? empty.fhrBPMHigh,
    fhrDurationOfListening: parsed.fhrDurationOfListening ?? empty.fhrDurationOfListening,
    fhrListeningDevice: parsed.fhrListeningDevice ?? empty.fhrListeningDevice,
    fhrRelationToContractions: parsed.fhrRelationToContractions ?? empty.fhrRelationToContractions,
    accelerationsDetected: parsed.accelerationsDetected ?? empty.accelerationsDetected,
    decelerationsDetected: parsed.decelerationsDetected ?? empty.decelerationsDetected,
    assessBaseline: parsed.assessBaseline ?? empty.assessBaseline,
    fhrBaseline: parsed.fhrBaseline ?? empty.fhrBaseline,
    fhrDurationOfListeningBaseline:
      parsed.fhrDurationOfListeningBaseline ?? empty.fhrDurationOfListeningBaseline,
    fhrNotes: parsed.fhrNotes ?? empty.fhrNotes,
    fhrSignatureInitials: parsed.fhrSignatureInitials ?? empty.fhrSignatureInitials,
  };
}
